import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IJackpotBetsInfoModel} from "../models/jackpot-bets-info.model.interfeace";
import {IJackpotBetsStrategy} from "./jackpot-bets-strategy.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {JackpotBetsInfoModel} from "../models/jackpot-bets-info.model";
import JackpotBetsFeedRawDataInterface from "../models/jackpot-bets-feed-raw-data.interface";
import {
    WheelOfJackpotbetDialogComponent
} from "../../../components/wheel-of-jackpotbet-dialog/wheel-of-jackpotbet-dialog.component";
import {WheelOfJackpotbetLogoBox} from "../../../components/wheel-of-jackpotbet-dialog/boxes/wheel-of-jb-logo/wheel-of-jb-logo.box";
import IFirebaseJackpotBetFeedsData
    from "../../../../common/interfaces/jackpot-bets/jp-bets-feed-update-firebase-data.interface";
import IFirebaseJackpotBetsWinNotificationData, {
    IFirebaseJackpotBetsErrorNotificationData,
    JackpotBetRejectionReason
} from "../../../../common/interfaces/jackpot-bets/jackpot-bets-player-notification-firebase-data.interface";
import {IFirebaseTopicRegistration} from "../../firebase/topics/firebase-topic-view-model.interface";
import {TimeSpan} from "@sparkware/app-utils";
import {imagePreload} from "../../../../common/utils/image-preload";
import {ButtonPreset} from "../../../basic-ui-components/button/base/button-props.interface";

export class AuthorizedJackpotBetsStrategy implements IJackpotBetsStrategy {
    constructor(private readonly services: IServiceFactoryExtended, initialJackpotBetsInfo: IJackpotBetsInfoModel | null) {
        makeObservable(this);
        this.jackpotBetsInfo = initialJackpotBetsInfo;
        this._firebaseRegistrations.push(services.firebase.topics.jackpotBetsFeeds.registerListener(this._onJackpotBetsFeedUpdate.bind(this)));
        this._firebaseRegistrations.push(services.firebase.topics.jackpotBetsWins.registerListener(this._onWinNotification.bind(this)));
        this._firebaseRegistrations.push(services.firebase.topics.jackpotBetsErrors.registerListener(this._onErrorNotification.bind(this)));
    }

    private _errorToasterActive: boolean = false;

    setErrorToasterActive(value: boolean): void {
        this._errorToasterActive = value;
    }

    private _firebaseRegistrations: IFirebaseTopicRegistration[] = [];

    @observable.ref
    jackpotBetsInfo: IJackpotBetsInfoModel | null = null;

    private _onJackpotBetsFeedUpdate(firebaseData: IFirebaseJackpotBetFeedsData): void {
        try {

            const rawData: JackpotBetsFeedRawDataInterface = {
                totalWinsToday: firebaseData.sumOfWins,
                winnersCount: firebaseData.numberOfWinners,
                jackpotsPrizes: firebaseData.jpAmounts.map(jp => {
                    return {
                        jackpotId: jp.JpId,
                        amount: jp.CurrentAmount
                    }
                })
            };

            if (this.jackpotBetsInfo) {
                this.jackpotBetsInfo.updateRawInfo(rawData);
            } else {
                runInAction(() => {
                    this.jackpotBetsInfo = new JackpotBetsInfoModel(rawData, this.services);
                });
            }

        } catch (err) {
            this.services.logger.error('Failed to parse jackpot bets feed from firebase', err);
        }
    }

    private _getErrorNotificationMessageBody(errorReasonId: JackpotBetRejectionReason, params: Record<string, string> = {}): string {
        let translationKey;
        switch (errorReasonId) {
            case JackpotBetRejectionReason.TemporaryProblem:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_1_2';
                break;
            case JackpotBetRejectionReason.GeneralError:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_1_2';
                break;
            case JackpotBetRejectionReason.BetWasFundedFromBonusOnly:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_4';
                break;
            case JackpotBetRejectionReason.InsufficientFunds:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_5';
                break;
            case JackpotBetRejectionReason.DailyWagerLimitReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_6';
                break;
            case JackpotBetRejectionReason.DailyLossLimitReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_7';
                break;
            case JackpotBetRejectionReason.WeeklyWagerLimitReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_8';
                break;
            case JackpotBetRejectionReason.WeeklyLossLimitReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_9';
                break;
            case JackpotBetRejectionReason.NonPeriodicLossLimitReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_10';
                break;
            case JackpotBetRejectionReason.MonthlyLossLimitReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_11';
                break;
            case JackpotBetRejectionReason.MonthlyWagerLimitReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_12';
                break;
            case JackpotBetRejectionReason.AmountExceededRegulationLimit:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_13';
                break;
            case JackpotBetRejectionReason.MaxStakeReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_14';
                break;
            default:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_GENERAL';
        }
        return this.services.translation.translate(translationKey, params);
    }

    private _shouldErrorNotificationHaveCTA(errorReasonId: JackpotBetRejectionReason): boolean {
        const reasonsWithCTA: JackpotBetRejectionReason[] = [
            JackpotBetRejectionReason.MinBetThresholdNotReached,
            JackpotBetRejectionReason.InsufficientFunds,
            JackpotBetRejectionReason.MaxStakeReached
        ];
        return reasonsWithCTA.includes(errorReasonId);
    }

    private _getErrorNotificationCTAConfig(errorReasonId: JackpotBetRejectionReason): {
        text: string;
        onClick: (closeToast: (closeReason?: string) => void) => void;
        preset: ButtonPreset;
    } | null {

        if (!this._shouldErrorNotificationHaveCTA(errorReasonId)) {
            return null;
        }

        return {
            preset: "info",
            text: this.services.translation.translate('JACKPOT_BETS_ERROR_TOASTER_CTA_TEXT'),
            onClick: closeToast => {
                this.services.jackpotBet.toggleJackpotBet().then(() => {
                    this.services.jackpotBet.sendJackpotBetEvent('error_popup_opt_out_click',
                        'from_err_toaster', `err_reason_${errorReasonId}`);
                    this.setErrorToasterActive(false);
                    closeToast();
                }).catch(error => {
                    this.services.jackpotBet.sendJackpotBetTechnicalEvent("status_toggle_failed", "src_error_popup", JSON.stringify(error));
                });
            }
        }
    }

    private async _onErrorNotification(firebaseData: IFirebaseJackpotBetsErrorNotificationData): Promise<void> {
        if (!firebaseData.failedReasonId) {
            this.services.jackpotBet.sendJackpotBetTechnicalEvent('error_toaster_not_shown', 'failed_reasonId_missing');
            return;
        }

        if (this._errorToasterActive) {
            //Product wants a single error toaster to be shown at a time, without stacking of notifications.
            this.services.jackpotBet.sendJackpotBetTechnicalEvent('error_toaster_not_shown',
                'previous_toaster_still_active', `new_err_reason_${firebaseData.failedReasonId}`);
            return;
        }

        let gameRunnerService = await this.services.gameRunner;
        let isInGame = Boolean(gameRunnerService.currentRunningGame);
        if (!isInGame) {
            return;
        }

        const messageBodyText: string = this._getErrorNotificationMessageBody(firebaseData.failedReasonId, {
            additionalStake: this.services.currency.formatMoney(firebaseData.participationFee)
        });

        this.services.toaster.showStandardToast({
            contentProps: {
                title: this.services.translation.translate('JACKPOT_BETS_ERROR_TOASTER_TITLE'),
                message: messageBodyText,
                button: this._getErrorNotificationCTAConfig(firebaseData.failedReasonId)
            },
            onToastShown: () => {
                this.setErrorToasterActive(true);
                this.services.jackpotBet.sendJackpotBetEvent('error_popup_view',
                    messageBodyText, `err_reason_${firebaseData.failedReasonId}`);
            },
            onToastClosed: () => {
                this.services.jackpotBet.sendJackpotBetEvent('error_popup_dismiss_click',
                    'x-button', `err_reason_${firebaseData.failedReasonId}`);
                this.setErrorToasterActive(false);
            }
        });
    }

    private _allowWinBelowBetCelebration(winAmount: number, participationFee: number): boolean {
        const allowWinBelowBetCelebration = !!this.services.configuration.data.jackpotBets?.allowWinBelowBetCelebration;
        return !allowWinBelowBetCelebration ? winAmount > participationFee : allowWinBelowBetCelebration;
    }

    private _onWinNotification(firebaseData: IFirebaseJackpotBetsWinNotificationData): void {
        const isLandscapeAndMobile = this.services.mediaQuery.landscape && this.services.mediaQuery.mAndBelow;
        const allowWinBelowBetCelebration = this._allowWinBelowBetCelebration(firebaseData.winAmount, firebaseData.parentGameBetAmount);
        const availablePrizes = this.services.jackpotBet.jackpotsInfo.getPrizes().filter(prize => Boolean(prize));
        if (availablePrizes.length === 0) {
            return;
        }
        const currentIndex = availablePrizes.findIndex(prize => prize.jackpotId === firebaseData.jackpotId);
        if (currentIndex === -1) {
            return;
        }

        this.services.jackpotBet.sendJackpotBetEvent('win_popup_view',
            JSON.stringify({
                'prize_index':currentIndex,
                'currency':this.services.currency.currencySymbol
            }),
            firebaseData.winAmount);

        Promise.allSettled([
            imagePreload(this.services.theme.currentTheme.assets.images.jackpot_bet.jackpot_bet_circle_svg.toString(), TimeSpan.fromSeconds(20)),
            imagePreload(this.services.theme.currentTheme.assets.images.wheel_of_jackpotbet.gold_center_png.toString(), TimeSpan.fromSeconds(20)),
            imagePreload(this.services.theme.currentTheme.assets.images.wheel_of_jackpotbet.flapper_png.toString(), TimeSpan.fromSeconds(20))
        ]).then(() => {
            this.services.dialog.showModal((dialogHandler) =>
                <div style={{position: "relative"}}>
                    {
                        isLandscapeAndMobile ? null : <WheelOfJackpotbetLogoBox />
                    }
                    <WheelOfJackpotbetDialogComponent
                        prizes={availablePrizes.map(prize => prize.label)}
                        winAmount={firebaseData.winAmount}
                        dialogHandler={dialogHandler}
                        winningLights={allowWinBelowBetCelebration}
                        winningConfetti={allowWinBelowBetCelebration}
                        prizeIndex={currentIndex}
                        onContinuePlaying={() => {
                            this.services.jackpotBet.sendJackpotBetEvent('win_popup_close',
                                `prize_index_${currentIndex}`, `prize_amount_${firebaseData.winAmount}`);
                        }}
                    />
                </div>, {
                hideBorders: true,
                hideDialogHeader: true,
                forceCentralization: true,
                disableFullscreenMobile: true,
                noDefaultContentPadding: true,
                isHighPriority: true,
                forceTransparentContentBackground: true,
            });
        });
    }

    dispose(): void {
        this._firebaseRegistrations.forEach(r => r.unregister());
        this._firebaseRegistrations = [];
    }
}